import React, { Dispatch, useRef, useCallback, useState } from 'react';

import { Form } from '@unform/web';
import {
  FiMail,
  FiLock,
  FiUser,
  FiArrowRight,
  FiArrowLeft,
} from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { FormHandles } from '@unform/core';
import * as Yup from 'yup';
import { FaArrowLeft } from 'react-icons/fa';
import { useAuth } from '../../../../hooks/Auth';
import Input from '../../../../components/Input';
import MaskedInput from '../../../../components/MaskedInput';
import { useToast } from '../../../../hooks/Toast';
import { AnimatedDiv } from '../../styles';
import { LoginContent } from './styles';
import getValidationErrors from '../../../../utils/getValidationErrors';
import SelectSimple from '../../../../components/SelectSimple';

import Button from '../../../../components/Button';
import { BuyContext } from '../../index';
import api from '../../../../services/api';
import TextareaInput from '../../../../components/TextareaInput';
import Loader from '../../../../components/Loader';

interface ComponentProps {
  step: number[];
  setStep: Dispatch<React.SetStateAction<number[]>>;
  buyContext: BuyContext;
  setBuyContext: Dispatch<React.SetStateAction<BuyContext>>;
}

interface SignInData {
  email: string;
  password: string;
}

interface LoginProps {
  [key: string]: string;
}

const LoginContainer: React.FC<ComponentProps> = ({
  buyContext,
  setBuyContext,
  setStep,
  step,
}) => {
  const { signIn } = useAuth();
  const { addToast } = useToast();
  const formRef = useRef<FormHandles>(null);
  const { productType } = buyContext;
  const [buyType, setBuyType] = useState('');

  const [loading, setLoading] = useState(false);

  const handleLogin = useCallback(
    async (data: SignInData) => {
      setLoading(true);
      try {
        formRef.current?.setErrors({});
        const schema = Yup.object().shape({
          email: Yup.string()
            .required('Email obrigatório')
            .email('Preencha um email válido'),
          password: Yup.string().required('Senha obrigatória'),
        });

        await schema.validate(data, { abortEarly: false });

        const { email, password } = data;

        await signIn({ email, password });
        setBuyContext({ ...buyContext, isLogged: true });
        setLoading(false);
        setStep([3, 4]);
        return;
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);
          formRef.current?.setErrors(errors);
          setLoading(false);
        }
        setLoading(false);
        addToast({
          type: 'error',
          title: 'Erro na autenticação',
          description: 'Ocorreu um erro ao fazer login',
        });
      }
    },
    [signIn, addToast, buyContext, setBuyContext, setStep],
  );

  const handleNewLogin = useCallback(
    async (data: LoginProps) => {
      setLoading(true);
      try {
        formRef.current?.setErrors({});
        const schema = Yup.object().shape({
          name: Yup.string().required('Nome obrigatório'),
          email: Yup.string()
            .required('Email obrigatório')
            .email('Preencha um email válido'),

          phone: Yup.string()
            .min(10, 'Telefone deve conter ao menos 10 caracteres')
            .max(11, 'Telefone deve conter no máximo 11 caracteres')
            .required('Telefone obrigatório'),
          password: Yup.string().required('Senha obrigatória'),

          document_number: Yup.string()
            .min(11, 'Preencha a CPF')
            .required('Número do CPF obrigatório'),

          birth_date: Yup.string()
            .min(10, 'min 10 Preecha a Data de Nascimento')
            .max(10, 'max 10 Preecha a Data de Nascimento')
            .required('Data de Nascimento obrigatória'),
          gender: Yup.string().required('Prencha o campo Sexo'),

          extra_4: Yup.string().required('Preencha o campo Categoria'),
        });
        data.phone = data.phone.toString().replace(/[^0-9]/g, '');
        data.birth_date.toString().replace(/[^0-9/]/g, '');
        data.document_number.toString().replace(/[^0-9]/g, '');

        await schema.validate(data, { abortEarly: false });

        const {
          name,
          email,
          password,

          phone,

          document_number,

          birth_date,

          extra_4,
          gender,
        } = data;

        const newUser = await api
          .post('/signup', {
            name,
            email,
            password,
            phone,
            document_type: 'cpf',
            document_number: document_number.toString().replace(/[^0-9]/g, ''),

            extra_4,
            gender,

            birth_date,
          })
          .catch(error => {
            if (error instanceof Yup.ValidationError) {
              const errors = getValidationErrors(error);
              formRef.current?.setErrors(errors);
              setLoading(false);
            }

            addToast({
              type: 'error',
              title: error.response.data.message,
            });
            setLoading(false);
            return false;
          });

        if (!newUser) {
          setLoading(false);
          return false;
        }
        await signIn({ email, password });
        setBuyContext({ ...buyContext, login: { name, email, password } });
        setLoading(false);
        setStep([3, 4]);

        return false;
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);

          formRef.current?.setErrors(errors);

          const errorInfo = err.errors.reduce((error, prev) => {
            prev = `${prev}, ${error}`;
            return prev;
          });
          setLoading(false);
          addToast({
            type: 'error',
            title: 'Erro de validação',
            description: errorInfo,
          });
        }
        setLoading(false);
      }
    },
    [addToast, buyContext, setBuyContext, setStep, signIn],
  );

  return (
    <AnimatedDiv visible={step[1] === 3}>
      <LoginContent>
        {loading && <Loader message="Carregando" />}
        <div>
          {' '}
          <aside>
            {buyType === '' && (
              <AnimatedDiv visible>
                <h2>Selecione a opção:</h2>
                <div style={{ display: 'flex' }}>
                  <Button
                    style={{ marginRight: '5px' }}
                    onClick={() => setBuyType('new')}
                  >
                    Novo Cadastro
                  </Button>
                  <Button onClick={() => setBuyType('login')}>
                    Já tenho cadastro
                  </Button>
                </div>
              </AnimatedDiv>
            )}
            {buyType !== '' && (
              <AnimatedDiv visible>
                <h3
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                  }}
                  onClick={() => setBuyType('')}
                >
                  <FaArrowLeft />
{' '}
Voltar
</h3>

                {buyType === 'login' && (
                  <Form ref={formRef} onSubmit={handleLogin}>
                    <h1>Login</h1>
                    <Input icon={FiMail} name="email" placeholder="E-mail" />
                    <Input
                      icon={FiLock}
                      name="password"
                      type="password"
                      placeholder="senha"
                    />
                    <Button type="submit">Entrar</Button>
                    <Link style={{ color: '#fff' }} to="/recuperar_senha">
                      Esqueci minha senha
                    </Link>
                  </Form>
                )}

                {buyType === 'new' && (
                  <Form ref={formRef} onSubmit={handleNewLogin}>
                    <h1>Novo cadastro</h1>

                    <Input name="name" placeholder="Nome completo" />
                    <Input name="email" placeholder="E-mail" />

                    <MaskedInput
                      mask="(99) 99999.9999"
                      name="phone"
                      placeholder="Telefone"
                    />

                    <MaskedInput
                      mask="999.999.999-99"
                      name="document_number"
                      placeholder="CPF"
                    />

                    <MaskedInput
                      mask="99/99/9999"
                      name="birth_date"
                      placeholder="Data de Nascimento"
                    />

                    <SelectSimple
                      options={[
                        { label: 'Masculino', value: 'Masculino' },
                        {
                          label: 'Feminino',
                          value: 'Feminino',
                        },
                        {
                          label: 'Não desejo informar',
                          value: 'Não desejo informar',
                        },
                      ]}
                      name="gender"
                      formRef={formRef}
                      label="Sexo"
                    />

                    <SelectSimple
                      options={[
                        { label: 'Advocacia', value: 'Advocacia' },
                        {
                          label: 'Bacharel em Direito',
                          value: 'Bacharel em Direito',
                        },
                        { label: 'Estudante', value: 'Estudante' },
                        { label: 'Imprensa', value: 'Imprensa' },
                        {
                          label: 'Outros profissionais',
                          value: 'Outros profissionais',
                        },
                      ]}
                      name="extra_4"
                      formRef={formRef}
                      label="Categoria"
                    />
                    <Input
                      icon={FiLock}
                      name="password"
                      type="password"
                      placeholder="senha"
                    />

                    <p
                      style={{
                        padding: '10px',
                        background: 'none',
                        margin: '5px auto',
                        color: '#fff',
                        fontSize: '12px',
                        textAlign: 'left',
                      }}
                    >
                      Ao me cadastrar, afirmo que li e aceito os
{' '}
                      <strong>
                        <Link
                          style={{ display: 'inline' }}
                          to="/termos"
                          target="_BLANK"
                        >
                          termos
{' '}
                        </Link>
{' '}
                      </strong>
                      para inscrição.
                    </p>
                    <Button type="submit">
                      {productType === 'free' ? 'Finalizar' : 'Continuar'}

                      <FiArrowRight />
                    </Button>
                  </Form>
                )}
              </AnimatedDiv>
            )}
          </aside>
        </div>
      </LoginContent>
    </AnimatedDiv>
  );
};

export default LoginContainer;
